/**
 * Agrupamento de pinos para a busca no mapa.
 *
 * Por que não usar `leaflet.markercluster`: o catálogo da iLaura tem 24 imóveis
 * na mesma coordenada (um condomínio inteiro na mesma avenida/CEP). O problema
 * real é esse empilhamento, e resolvê-lo cabe em ~60 linhas testáveis -- menos
 * do que carregar um plugin com CSS próprio e API imperativa dentro do React.
 *
 * O agrupamento é feito em pixels de tela, não em graus: dois imóveis a 500 m
 * se sobrepõem no zoom da cidade e não se sobrepõem no zoom da rua.
 */

export interface MapPin {
  id: string;
  title: string;
  type: string;
  transaction_type?: string;
  price?: number;
  cover_image?: string;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  parking_spaces?: number;
  city?: string;
  neighborhood?: string;
  latitude: number;
  longitude: number;
  precision?: string;
}

export interface PinCluster {
  id: string;
  latitude: number;
  longitude: number;
  pins: MapPin[];
}

export interface MapBounds {
  south: number;
  north: number;
  west: number;
  east: number;
}

const TILE_SIZE = 256;

/** Distância mínima, em pixels, entre dois pinos antes de virarem um grupo. */
export const CLUSTER_CELL_PX = 68;

/** Web Mercator, o mesmo do Leaflet: leste → +x, norte → −y. */
export function projectToPixel(latitude: number, longitude: number, zoom: number) {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const sinLat = Math.sin((latitude * Math.PI) / 180);
  return {
    x: ((longitude + 180) / 360) * scale,
    y:
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
}

export function clusterPins(
  pins: MapPin[],
  zoom: number,
  cellSize: number = CLUSTER_CELL_PX
): PinCluster[] {
  const cells = new Map<string, MapPin[]>();

  pins.forEach(pin => {
    const { x, y } = projectToPixel(pin.latitude, pin.longitude, zoom);
    const key = `${Math.floor(x / cellSize)}:${Math.floor(y / cellSize)}`;
    const cell = cells.get(key);
    if (cell) cell.push(pin);
    else cells.set(key, [pin]);
  });

  return Array.from(cells.entries()).map(([key, group]) => ({
    id: `${key}@${zoom}`,
    // O grupo fica no centro dos seus pinos, não no centro da célula: assim ele
    // não "salta" para um lugar onde não há imóvel nenhum.
    latitude: group.reduce((total, p) => total + p.latitude, 0) / group.length,
    longitude: group.reduce((total, p) => total + p.longitude, 0) / group.length,
    pins: group,
  }));
}

export function pinsWithin(pins: MapPin[], bounds: MapBounds | null): MapPin[] {
  if (!bounds) return pins;
  return pins.filter(
    pin =>
      pin.latitude >= bounds.south &&
      pin.latitude <= bounds.north &&
      pin.longitude >= bounds.west &&
      pin.longitude <= bounds.east
  );
}

/** Meio grau (~55 km): o raio de atuação de uma imobiliária de cidade média. */
const FOCUS_DEGREES = 0.5;

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * Área para abrir o mapa na primeira carga.
 *
 * Enquadrar TODOS os pinos abriria o mapa no estado inteiro por causa de um
 * imóvel em Assis, a 250 km dos outros 115 -- e a pessoa começaria a busca
 * olhando para rodovia. O enquadramento segue a mediana; o imóvel distante
 * continua no mapa, só não manda no zoom inicial.
 */
export function initialFocusBounds(pins: MapPin[]): MapBounds | null {
  if (pins.length === 0) return null;

  const centerLat = median(pins.map(p => p.latitude));
  const centerLng = median(pins.map(p => p.longitude));
  const core = pins.filter(
    p =>
      Math.abs(p.latitude - centerLat) <= FOCUS_DEGREES &&
      Math.abs(p.longitude - centerLng) <= FOCUS_DEGREES
  );
  const used = core.length > 0 ? core : pins;

  return {
    south: Math.min(...used.map(p => p.latitude)),
    north: Math.max(...used.map(p => p.latitude)),
    west: Math.min(...used.map(p => p.longitude)),
    east: Math.max(...used.map(p => p.longitude)),
  };
}

/**
 * Cidade onde está a maior parte do catálogo.
 *
 * O texto da página não pode citar uma cidade fixa: a plataforma é
 * multi-tenant e o que é "Rio Preto e região" para uma imobiliária é outra
 * cidade para a seguinte. Quem responde isso é o catálogo do tenant.
 */
export function mainCityOf(pins: MapPin[]): string | null {
  const counts = new Map<string, number>();
  pins.forEach(pin => {
    const city = (pin.city || '').trim();
    if (city) counts.set(city, (counts.get(city) || 0) + 1);
  });

  let winner: string | null = null;
  let best = 0;
  counts.forEach((count, city) => {
    if (count > best) {
      best = count;
      winner = city;
    }
  });
  return winner;
}
